import { Pressable, StyleSheet } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconSize, IconTone } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { useTheme } from '../theme/ThemeProvider';

export type IconButtonVariant = 'plain' | 'surface' | 'primary';

export type IconButtonProps = {
  icon: IconName;
  onPress?: () => void | Promise<void>;
  accessibilityLabel?: string;
  size?: IconSize | number;
  tone?: IconTone;
  variant?: IconButtonVariant;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function IconButton({
  icon,
  onPress,
  accessibilityLabel,
  size = 'md',
  tone = 'default',
  variant = 'plain',
  disabled = false,
  style,
  testID,
}: IconButtonProps) {
  const theme = useTheme();
  const effectiveTone = variant === 'primary' ? 'inverse' : tone;

  const surfaceStyle: ViewStyle =
    variant === 'plain'
      ? {}
      : {
          backgroundColor: variant === 'primary' ? theme.colors.primary : theme.colors.surfaceAlt,
          borderRadius: theme.radius.pill,
        };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      aria-disabled={disabled}
      disabled={disabled || onPress === undefined}
      onPress={() => void onPress?.()}
      testID={testID}
      style={({ pressed }) => [
        styles.button,
        { padding: theme.spacing(2) },
        surfaceStyle,
        disabled && styles.disabled,
        pressed && !disabled && styles.pressed,
        style,
      ]}
    >
      <Icon name={icon} size={size} tone={effectiveTone} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: { alignItems: 'center', justifyContent: 'center' },
  disabled: { opacity: 0.4 },
  pressed: { opacity: 0.6 },
});
